import { useEffect, useMemo, useState } from "react";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { TABS, type TabKey } from "@/components/layout/Sidebar";
import { useUrlValue } from "@/hooks/useUrlState";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useDisplaySettings } from "@/hooks/useDisplaySettings";
import { api } from "@/lib/api";
import { Overview } from "@/routes/Overview";
import { UnderAttackMode } from "@/routes/UnderAttackMode";
import { Campaigns } from "@/routes/Campaigns";
import { Investigations } from "@/routes/Investigations";
import { Trends } from "@/routes/Trends";
import { ScorerHealth } from "@/routes/ScorerHealth";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // We drive refresh through useAutoRefresh + queryKey invalidation.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 25_000,
    },
  },
});

const VALID_TABS = new Set<TabKey>(TABS.map((t) => t.key));

/** Narrow per-entity filter keys wiped on every plain sidebar nav. */
const NARROW_KEYS = [
  "asn", "ip", "ja4", "ja3", "email", "businessId", "sessionId",
  "country", "colo", "endpointGroups", "endpointType", "pathPrefix",
  "method", "riskBands", "scoreMin", "scoreMax", "managedList",
  "detectionId", "reasonContains", "userAgentContains", "action",
  "outcome", "botScoreBucket", "threatScoreMin", "threatScoreMax",
  "verifiedBot", "entity",
];

function navigate(tab: TabKey) {
  // Clicking a sidebar tab is a fresh start: drop every narrow filter
  // and the entity drawer param so the destination tab loads with the
  // user's broad time window only. Cross-tab pivots from charts /
  // tables go through `pivotTo` which sets the keys it needs after
  // this same wipe, so this is safe for both flows.
  const url = new URL(window.location.href);
  for (const k of NARROW_KEYS) url.searchParams.delete(k);
  url.searchParams.set("tab", tab);
  window.history.pushState(null, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function Inner() {
  useDisplaySettings();
  const [tabParam] = useUrlValue("tab");
  const tabRaw = tabParam ?? "overview";
  const active = (VALID_TABS.has(tabRaw as TabKey) ? tabRaw : "overview") as TabKey;
  const tabMeta = useMemo(() => TABS.find((t) => t.key === active)!, [active]);

  const { tick } = useAutoRefresh();

  // Ensure the bootstrap meta refreshes alongside other data.
  const meta = useQuery({
    queryKey: ["meta", tick],
    queryFn: ({ signal }) => api.meta(signal).then((r) => r.data),
  });

  return (
    <AppShell
      active={active}
      onNavigate={navigate}
      title={tabMeta.label}
      description={tabMeta.description}
      environment={meta.data?.environment}
      mockMode={meta.data?.mockMode}
      generatedAt={undefined}
      showFilterBar={active !== "scorerHealth"}
    >
      {active === "overview"       && <Overview />}
      {active === "underAttackMode" && <UnderAttackMode />}
      {active === "campaigns"      && <Campaigns />}
      {active === "investigations" && <Investigations />}
      {active === "trends"         && <Trends />}
      {active === "scorerHealth"   && <ScorerHealth />}
    </AppShell>
  );
}

export default function App() {
  // Default tab shown in URL on first load — keeps shareable links honest.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.get("tab")) {
      url.searchParams.set("tab", "overview");
      window.history.replaceState(null, "", url);
    }
  }, []);

  // Keep `tick` working without a hook subscriber.
  const [, force] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => force((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Inner />
    </QueryClientProvider>
  );
}
